import { InboxOutlined, WarningOutlined } from "@ant-design/icons";
import { Alert, Image, Modal, Space, Tag, Typography } from "antd";
import { useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  getTripStartCheckErrorKey,
  ITripStartCheck,
  ITripStartCheckLine,
  parseTripStartCheck,
  TRIP_START_CHECK_REQUIRED_ERROR_KEY,
} from "../../../models/trip/start-check";
import EndPoints from "../../../services/end-points";
import { formatRecordReservedQuantityWithUnit } from "../../../utils/helpers/quantity";
import styles from "./style.module.scss";

/** Fetch a fresh start-check snapshot (quantities are live server-side). */
export const fetchTripStartCheck = async (
  tripId: number,
): Promise<ITripStartCheck> => {
  const { data } = await EndPoints.trip.getTripStartCheck(tripId);
  return parseTripStartCheck(data);
};

export const isTripStartCheckRequiredError = (error: any): boolean =>
  getTripStartCheckErrorKey(error) === TRIP_START_CHECK_REQUIRED_ERROR_KEY;

export const isAmbiguousTripRequestError = (error: any): boolean => {
  const status = error?.response?.status;
  return !status || status === 408 || (status >= 500 && status <= 599);
};

interface IProps {
  open: boolean;
  check: ITripStartCheck | null;
  confirming?: boolean;
  reviewMessage?: string | null;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

const TripStartCheckModal: React.FC<IProps> = ({
  open,
  check,
  confirming,
  reviewMessage,
  onConfirm,
  onCancel,
}) => {
  const { t, i18n } = useTranslation();
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const isArabic = (i18n.language || "").startsWith("ar");
  const reviewSnapshotKey =
    open && check?.required
      ? JSON.stringify([check.startCheckToken, reviewMessage ?? ""])
      : null;

  useLayoutEffect(() => {
    if (!reviewSnapshotKey || !modalBodyRef.current) return;
    modalBodyRef.current.scrollTop = 0;
  }, [reviewSnapshotKey]);
  const localized = (
    value?: { ar?: string | null; en?: string | null } | string | null,
  ): string => {
    if (!value) return "";
    if (typeof value === "string") return value;
    return (isArabic ? value.ar : value.en) ?? value.ar ?? value.en ?? "";
  };

  // Adapt the thin line to the shared product-order quantity shape. Older
  // responses can omit the top-level unit, so use the partition base unit
  // as a fallback, never the partition label itself.
  const getLineQuantityText = (line: ITripStartCheckLine): string => {
    const partitionBaseUnit = line.unitPartition?.unit;
    const factorAccountingSystem =
      line.factorAccountingSystem ??
      partitionBaseUnit?.factorAccountingSystem ??
      partitionBaseUnit?.factor_accounting_system;
    const decimalPlaces =
      line.decimalPlaces ?? partitionBaseUnit?.decimalPlaces;
    const unitName =
      localized(line.unit?.name) || partitionBaseUnit?.name || "";

    return formatRecordReservedQuantityWithUnit({
      reservedQuantityByUnit: line.reservedQuantityByUnit,
      quantityByUnit: line.quantityByUnit,
      reservedQuantityAccounting: line.reservedQuantityAccounting,
      quantityAccounting: line.quantityAccounting,
      count: line.count,
      unitPartition: line.unitPartition,
      factorAccountingSystem,
      decimalPlaces,
      product: {
        unit: {
          factorAccountingSystem,
          factor_accounting_system: factorAccountingSystem,
          decimalPlaces,
          name: unitName,
        },
      },
    });
  };

  const handleCancel = () => {
    if (!confirming) onCancel();
  };

  return (
    <Modal
      title={
        <Space>
          <WarningOutlined style={{ color: "#faad14" }} />
          {t("trip_start_check_title")}
        </Space>
      }
      open={open}
      onOk={onConfirm}
      onCancel={handleCancel}
      okText={t("trip_start_check_confirm_button")}
      cancelText={t("cancel")}
      confirmLoading={confirming}
      cancelButtonProps={{ disabled: confirming }}
      closable={!confirming}
      keyboard={!confirming}
      maskClosable={false}
      width={640}
    >
      <div ref={modalBodyRef} className={styles.modalBody}>
        {reviewMessage ? (
          <Alert
            className={styles.reviewAlert}
            type="warning"
            showIcon
            message={reviewMessage}
          />
        ) : null}
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Alert
            type="warning"
            showIcon
            icon={<InboxOutlined />}
            message={t("trip_start_check_intro")}
          />

          {check?.orders.map((order) => (
            <div key={order.orderId} className={styles.orderBlock}>
              <div className={styles.orderHeader}>
                <span>
                  {t("order_number")}: {order.orderNumber}
                </span>
                {order.clientName && (
                  <Typography.Text type="secondary">
                    {t("client")}: {order.clientName}
                  </Typography.Text>
                )}
              </div>
              {order.lines.map((line) => (
                <div key={line.productOrderId} className={styles.lineRow}>
                  <div className={styles.lineImage}>
                    {line.image?.url ? (
                      <Image
                        src={line.image.url}
                        width={64}
                        height={64}
                        style={{ objectFit: "contain" }}
                        preview={false}
                      />
                    ) : (
                      <InboxOutlined style={{ fontSize: 28, opacity: 0.35 }} />
                    )}
                  </div>
                  <div className={styles.lineInfo}>
                    <Typography.Text strong ellipsis>
                      {localized(line.productName) || `#${line.productId}`}
                    </Typography.Text>
                    <div className={styles.lineBadges}>
                      {line.isAlternative && (
                        <Tag color="purple">{t("cash_check_alternative")}</Tag>
                      )}
                      {line.isAddedFromStore && (
                        <Tag color="green">{t("added_from_store")}</Tag>
                      )}
                    </div>
                  </div>
                  <span className={styles.lineQuantity}>
                    {getLineQuantityText(line)}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </Space>
      </div>
    </Modal>
  );
};

export default TripStartCheckModal;
